import { createApi } from '@reduxjs/toolkit/query/react';
import B2ChatClient, { apiResource, b2chatBaseQuery } from '@src/client';

// --- Types ---

export type WebhookEventType = {
  code: string;
  name: string;
  description: string;
};

export type WebhookSubscription = {
  merchant_id: number;
  target_url: string;
  bearer_token_masked: string;
  error_report_email: string;
  is_active: boolean;
  event_types: WebhookEventType[];
  created_at: number;
  updated_at: number;
};

export type CreateSubscriptionRequest = {
  target_url: string;
  bearer_token?: string;
  error_report_email: string;
  event_types: string[];
  locale?: string;
};

export type UpdateSubscriptionRequest = {
  target_url?: string;
  bearer_token?: string;
  error_report_email?: string;
  event_types?: string[];
  locale?: string;
};

export type UpdateSubscriptionStatusRequest = {
  is_active: boolean;
};

export type GetTestPayloadRequest = {
  event_type: string;
};

export type TestPayload = {
  specversion: string;
  type: string;
  source: string;
  id: string;
  time: string;
  merchant_id: number;
  data: Record<string, unknown>;
};

export type SendTestRequest = {
  event_type: string;
};

export type SendTestResponse = {
  delivered: boolean;
  status_code?: number;
  response_body?: string;
  latency_ms?: number;
  payload?: TestPayload;
  error?: string;
};

// --- API ---

const webhookApi = createApi({
  reducerPath: 'webhookApi',
  baseQuery: b2chatBaseQuery,
  tagTypes: ['webhook-subscription', 'webhook-event-types'],
  endpoints: ({ query, mutation }) => ({
    getSubscription: query<
      WebhookSubscription | undefined,
      { locale?: string } | void
    >({
      providesTags: ['webhook-subscription'],
      extraOptions: { maxRetries: 0 },
      // A failed response means no subscription is configured — not an error.
      async queryFn(args, _api, _extraOptions, baseQuery) {
        const result = await baseQuery(
          apiResource(B2ChatClient.resources.webhooks.actions.getSubscription, {
            headers: args?.locale
              ? { 'Accept-Language': args.locale }
              : undefined,
          })
        );
        if (result.error) return { data: undefined };
        return { data: result.data as WebhookSubscription };
      },
    }),

    createSubscription: mutation<
      WebhookSubscription,
      CreateSubscriptionRequest
    >({
      invalidatesTags: ['webhook-subscription'],
      query: ({ locale, ...body }) =>
        apiResource(
          B2ChatClient.resources.webhooks.actions.createSubscription,
          {
            data: body,
            headers: locale ? { 'Accept-Language': locale } : undefined,
          }
        ),
    }),

    updateSubscription: mutation<
      WebhookSubscription,
      UpdateSubscriptionRequest
    >({
      invalidatesTags: ['webhook-subscription'],
      query: ({ locale, ...body }) =>
        apiResource(
          B2ChatClient.resources.webhooks.actions.updateSubscription,
          {
            data: body,
            headers: locale ? { 'Accept-Language': locale } : undefined,
          }
        ),
    }),

    deleteSubscription: mutation<void, void>({
      invalidatesTags: ['webhook-subscription'],
      query: () =>
        apiResource(
          B2ChatClient.resources.webhooks.actions.deleteSubscription,
          {}
        ),
    }),

    updateSubscriptionStatus: mutation<void, UpdateSubscriptionStatusRequest>({
      invalidatesTags: ['webhook-subscription'],
      query: body =>
        apiResource(
          B2ChatClient.resources.webhooks.actions.updateSubscriptionStatus,
          { data: body }
        ),
    }),

    getTestPayload: query<TestPayload, GetTestPayloadRequest>({
      extraOptions: { maxRetries: 0 },
      // eslint-disable-next-line camelcase
      query: ({ event_type }) =>
        apiResource(B2ChatClient.resources.webhooks.actions.getTestPayload, {
          // eslint-disable-next-line camelcase
          params: { event_type },
        }),
    }),

    sendTestPayload: mutation<SendTestResponse, SendTestRequest>({
      extraOptions: { maxRetries: 0 },
      query: body =>
        apiResource(B2ChatClient.resources.webhooks.actions.sendTestPayload, {
          data: body,
        }),
    }),

    getEventTypes: query<WebhookEventType[], { locale?: string } | void>({
      providesTags: ['webhook-event-types'],
      query: args =>
        apiResource(B2ChatClient.resources.webhooks.actions.getEventTypes, {
          headers: args?.locale
            ? { 'Accept-Language': args.locale }
            : undefined,
        }),
    }),
  }),
});

export const {
  useGetSubscriptionQuery,
  useLazyGetSubscriptionQuery,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useUpdateSubscriptionStatusMutation,
  useGetTestPayloadQuery,
  useLazyGetTestPayloadQuery,
  useSendTestPayloadMutation,
  useGetEventTypesQuery,
  useLazyGetEventTypesQuery,
} = webhookApi;

export default webhookApi;
